import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Calculator, Camera, Eye, FileUp, Loader2, RefreshCw, ScanText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FieldInput } from "@/components/FieldInput";
import { api, errMsg, openPdf } from "@/lib/api";

const CF_RE = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;
const FEST_KEYS = ["cognome", "nome", "sesso", "data_nascita", "luogo_nascita", "cf"];

export function Uploader({ p, busy, onFile }) {
  const cam = useRef(null);
  const file = useRef(null);
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) onFile(f);
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={busy} onClick={() => cam.current.click()} data-testid={`id-${p}-camera-button`}><Camera size={14} className="mr-1" /> Scatta foto</Button>
      <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={busy} onClick={() => file.current.click()} data-testid={`id-${p}-file-button`}><FileUp size={14} className="mr-1" /> Carica foto o PDF</Button>
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`id-${p}-camera-input`} />
      <input ref={file} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid={`id-${p}-file-input`} />
    </div>
  );
}

export function PersonCard({ cid, slot, data, setFields, editable, tpl, slotLabels = {} }) {
  const [p, label, mandatory] = slot;
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const files = data[`${p}_files`] || [];
  const fields = p === "fest" ? tpl.id_fields.filter((f) => FEST_KEYS.includes(f.k)).map((f) => (f.k === "cf" ? { ...f, required: false } : f)) : tpl.id_fields;
  const get = (k) => data[`${p}_${k}`];
  const cf = (get("cf") || "").toUpperCase();
  const expired = get("doc_scadenza") && get("doc_scadenza") < new Date().toISOString().slice(0, 10);
  const sidesStr = files.map((f) => f.side || "").join(" ");
  const sidesOk = sidesStr.includes("fronte") && sidesStr.includes("retro");
  const needBack = p !== "fest" && files.length > 0 && !busy && (!sidesOk || !get("indirizzo"));
  const needCf = p !== "fest" && files.length > 0 && !busy && !needBack && get("cf_doc") === "no" && !files.some((f) => f.side === "codice fiscale");
  const changeSide = async (fid, side) => {
    try {
      const r = await api.patch(`/contracts/${cid}/identity/${p}/files/${fid}`, { side });
      setFields({ [`${p}_files`]: r.data.files });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const apply = (res, overwrite) => {
    const upd = { [`${p}_files`]: res.files, ...(res.fields && Object.keys(res.fields).length ? { [`${p}_cf_doc`]: res.cf_documento ? "si" : "no" } : {}) };
    const fill = (q, fieldsObj, force) => Object.entries(fieldsObj || {}).forEach(([k, v]) => {
      if (v && (force || !data[`${q}_${k}`])) upd[`${q}_${k}`] = v;
    });
    fill(p, res.fields, overwrite);
    const notes = [...(res.warnings || [])];
    (res.others || []).forEach((o) => {
      fill(o.person, o.fields, false);
      upd[`${o.person}_files`] = o.files;
      upd[`${o.person}_cf_doc`] = o.cf_documento ? "si" : "no";
      if (o.person === "id2") upd.show_id2 = true;
      const who = `${o.fields?.nome || ""} ${o.fields?.cognome || ""}`.trim();
      notes.unshift(`Nello stesso file ci sono anche i dati di ${who}: inseriti nella scheda «${slotLabels[o.person] || o.person}», controllali.`);
      (o.warnings || []).forEach((w) => notes.push(`${slotLabels[o.person] || o.person}: ${w}`));
    });
    setFields(upd);
    setWarnings(notes);
  };
  const run = async (fn, overwrite, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      apply(r.data, overwrite);
      toast.success(okMsg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const upload = (f, side) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("side", side || (files.length === 0 ? "fronte" : "retro"));
    run(() => api.post(`/contracts/${cid}/identity/${p}/upload`, fd), false, "Documento caricato e letto");
  };
  const remove = async (fid) => {
    try {
      const r = await api.delete(`/contracts/${cid}/identity/${p}/files/${fid}`);
      setFields({ [`${p}_files`]: r.data.files });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const calc = async () => {
    try {
      const r = await api.post("/codice-fiscale", { cognome: get("cognome") || "", nome: get("nome") || "", sesso: get("sesso") || "", data_nascita: get("data_nascita") || "", luogo_nascita: get("luogo_nascita") || "" });
      setFields({ [`${p}_cf`]: r.data.cf });
      toast.success(r.data.cf === cf ? "Codice fiscale confermato" : `Codice fiscale calcolato: ${r.data.cf}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <section className="rounded-xl border border-[#D4E6EC] bg-white p-5 sm:p-7 space-y-5 fade-up" data-testid={`person-card-${p}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif-it text-2xl font-semibold">{label}</h3>
          <p className="text-xs text-[#47606B] mt-1">{mandatory && p !== "fest" ? "Documento d'identità obbligatorio (fronte e retro)." : "Documento facoltativo."} Puoi caricare anche un'unica scansione con i documenti di entrambi: l'IA li separa. Verifica sempre i dati letti.</p>
        </div>
        {editable && <Uploader p={p} busy={busy} onFile={upload} />}
      </div>
      {busy && <div className="flex items-center gap-2 text-sm text-[#075985] bg-[#E0F2FE] rounded-md p-3" data-testid={`id-${p}-reading`}><Loader2 size={15} className="animate-spin" /> Lettura del documento in corso…</div>}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`id-${p}-files`}>
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-xs rounded-full border border-[#D4E6EC] bg-[#EAF5F7] pl-3 pr-1 py-1">
              <select value={f.side || "fronte"} disabled={!editable} onChange={(e) => changeSide(f.id, e.target.value)} data-testid={`id-${p}-file-side-${f.id}`}
                className="bg-transparent font-medium focus:outline-none">
                <option value="fronte">Fronte</option><option value="retro">Retro</option><option value="fronte e retro">Fronte e retro</option><option value="codice fiscale">Codice fiscale</option>
              </select>
              <span className="text-[#47606B] max-w-[160px] truncate">{f.name}</span>
              {f.scan && <button className="px-1.5 py-0.5 hover:bg-white rounded-full text-[10px] font-semibold text-[#0D2B3A] border border-[#0D2B3A]/30" title="Scansione in bianco e nero" onClick={() => openPdf(`/files/${f.id}/scan`)} data-testid={`id-${p}-file-scan-${f.id}`}>B/N</button>}
              <button className="p-1 hover:bg-white rounded-full" onClick={() => openPdf(`/files/${f.id}/download`)} data-testid={`id-${p}-file-open-${f.id}`}><Eye size={13} /></button>
              {editable && <button className="p-1 hover:bg-white rounded-full text-[#991B1B]" onClick={() => remove(f.id)} data-testid={`id-${p}-file-remove-${f.id}`}><Trash2 size={13} /></button>}
            </div>
          ))}
          {editable && <Button variant="ghost" size="sm" disabled={busy} onClick={() => run(() => api.post(`/contracts/${cid}/identity/${p}/extract`), true, "Documento riletto")} data-testid={`id-${p}-reextract`}><RefreshCw size={13} className="mr-1" /> Rileggi con IA</Button>}
        </div>
      )}
      {needBack && (
        <div className="rounded-md border border-[#34B3B0] bg-[#FFF8E7] p-4 text-sm space-y-3" data-testid={`id-${p}-need-back`}>
          <div className="flex gap-2 font-medium text-[#0B6E8F]"><AlertTriangle size={16} className="shrink-0 mt-0.5" />
            {!sidesOk ? "Manca il retro del documento: per completare il contratto servono le copie fronte e retro." : "Residenza non presente sul lato caricato: scansiona il retro del documento."}
          </div>
          {editable && <Uploader p={`${p}-back`} busy={busy} onFile={upload} />}
        </div>
      )}
      {needCf && (
        <div className="rounded-md border border-[#34B3B0] bg-[#FFF8E7] p-4 text-sm space-y-3" data-testid={`id-${p}-need-cf`}>
          <div className="flex gap-2 font-medium text-[#0B6E8F]"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> Il documento non riporta il codice fiscale: scansiona il fronte della tessera sanitaria / codice fiscale.</div>
          {editable && <Uploader p={`${p}-cf`} busy={busy} onFile={(f) => upload(f, "codice fiscale")} />}
        </div>
      )}
      {(warnings.length > 0 || expired) && (
        <div className="rounded-md bg-[#FEF3C7] text-[#92400E] p-3 text-sm space-y-1" data-testid={`id-${p}-warnings`}>
          {expired && <div className="flex gap-2"><AlertTriangle size={15} className="shrink-0 mt-0.5" /> Documento scaduto: richiedere un documento valido.</div>}
          {warnings.map((w) => <div key={w} className="flex gap-2"><AlertTriangle size={15} className="shrink-0 mt-0.5" /> {w}</div>)}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        {fields.map((f) => (
          <FieldInput key={f.k} f={{ ...f, k: `${p}_${f.k}` }} value={get(f.k)} disabled={!editable}
            onChange={(v) => setFields({ [`${p}_${f.k}`]: f.k === "cf" ? v.toUpperCase().replace(/\s/g, "") : v })} />
        ))}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {editable && <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={calc} data-testid={`id-${p}-calc-cf`}><Calculator size={14} className="mr-1" /> Calcola / verifica codice fiscale</Button>}
        {cf && <span className={`text-xs font-medium ${CF_RE.test(cf) ? "text-[#166534]" : "text-[#991B1B]"}`} data-testid={`id-${p}-cf-status`}>{CF_RE.test(cf) ? "Formato codice fiscale corretto" : "Formato codice fiscale non valido"}</span>}
        {!busy && files.length === 0 && editable && <span className="text-xs text-[#7B929C] inline-flex items-center gap-1"><ScanText size={13} /> Fotografa il documento per compilare in automatico</span>}
      </div>
    </section>
  );
}
